import { CardColor } from '../types';

type PackName = 'classic' | 'dark' | 'fantasy';

const PLACEHOLDER = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR4nGMAAQAABQABDQottAAAAABJRU5ErkJggg==';
const DEFERRED_EMOTE_IDS = ['anger', 'piece', 'sad', 'shocked', 'smirk', 'thanks'] as const;
const DEFERRED_AUDIO_URLS = [
  '/OST/Win.ogg',
  '/OST/loose.ogg',
  '/OST/battle0.ogg',
  '/OST/battle1.ogg',
  '/OST/battle2.ogg',
  '/OST/battle3.ogg',
  '/OST/battle4.ogg',
  '/OST/battle5.ogg',
];
const DEFERRED_EFFECT_URLS = [
  '/assets/effects/hammer_slam/hammer_slam_sheet.png',
  '/assets/effects/voodoo_doll/voodoo_doll_anim.png',
];
const DEFERRED_PROFILE_SOURCE_PATHS = [
  '../assets/ui/fantasy/1lvl.png',
  '../assets/ui/fantasy/2lvl.png',
  '../assets/ui/fantasy/3lvl.png',
  '../assets/ui/fantasy/4lvl.png',
  '../assets/ui/fantasy/5lvl.png',
  '../assets/ui/fantasy/6lvl.png',
  '../assets/ui/fantasy/7lvl.png',
  '../assets/ui/fantasy/8lvl.png',
  '../assets/ui/fantasy/9lvl.png',
  '../assets/ui/fantasy/10lvl.png',
  '../assets/ui/fantasy/11lvl.png',
];

let currentPack: PackName = (typeof window !== 'undefined' && (localStorage.getItem('uiPack') as PackName)) || 'classic';

export function setUiPack(pack: PackName) {
  currentPack = pack;
  if (typeof window !== 'undefined') localStorage.setItem('uiPack', pack);
}

export function getUiPack(): PackName {
  return currentPack;
}

// Node.js compatibility: import.meta.glob is Vite-specific and doesn't exist in Node.js
// IMPORTANT: import.meta.glob MUST be called statically - Vite transforms it at build time
// We use a try-catch because in Node.js (headless mode), glob doesn't exist and will throw
let files: Record<string, any> = {};
try {
  // This static call is transformed by Vite at build time
  files = (import.meta as any).glob('../assets/**/*', { eager: true }) || {};
} catch {
  // In Node.js/headless mode, import.meta.glob doesn't exist - that's OK, we don't need UI assets
  files = {};
}

const urlMap: Record<string, string> = {};
for (const p in files) {
  const mod = files[p] as any;
  const url = mod?.default as string | undefined;
  if (url) urlMap[p] = url;
}

const webpToPngMap = new Map<string, string>();

function buildWebpPngMap() {
  const pngEntries: [string, string][] = [];
  const webpEntries: [string, string][] = [];

  for (const [srcPath, distUrl] of Object.entries(urlMap)) {
    if (srcPath.endsWith('.png')) pngEntries.push([srcPath, distUrl]);
    if (srcPath.endsWith('.webp')) webpEntries.push([srcPath, distUrl]);
  }

  for (const [webpSrc, webpUrl] of webpEntries) {
    const normalizedWebp = webpSrc.replace(/\.webp$/, '');
    const pngEntry = pngEntries.find(([pngSrc]) => pngSrc.replace(/\.png$/, '') === normalizedWebp);
    if (pngEntry) {
      webpToPngMap.set(webpUrl, pngEntry[1]);
    }
  }
}

buildWebpPngMap();

function getImportedAssetUrl(path: string | null): string | null {
  if (path && urlMap[path]) {
    return urlMap[path];
  }
  return null;
}

function colorSlug(color: CardColor): string {
  switch (color) {
    case CardColor.Red: return 'red';
    case CardColor.Pink: return 'pink';
    case CardColor.Green: return 'green';
    case CardColor.Black: return 'black';
    case CardColor.Purple: return 'purple';
    case CardColor.Yellow: return 'yellow';
    default: return 'red';
  }
}

function keyToPath(key: string, pack: PackName, dprSuffix: string): string | null {
  const parts = key.split('.');
  if (parts[0] === 'ui' && parts[1] === 'table') {
    const name = parts[2] === 'bgB' ? 'table_bg_b' : 'table_bg_a';
    return `../assets/ui/${pack}/${name}${dprSuffix}.png`;
  }
  if (parts[0] === 'ui' && parts[1] === 'menu') {
    if (parts[2] === 'bg') return `../assets/ui/${pack}/menu_bg${dprSuffix}.png`;
    if (parts[2] === 'btnPrimary') return `../assets/ui/${pack}/menu_btn_primary${dprSuffix}.png`;
  }
  if (parts[0] === 'ui' && parts[1] === 'hud' && parts[2] === 'heart') {
    return `../assets/ui/${pack}/heart${dprSuffix}.png`;
  }
  if (parts[0] === 'ui' && parts[1] === 'hud' && parts[2] === 'shield') {
    return `../assets/ui/${pack}/shield${dprSuffix}.png`;
  }

  if (parts[0] === 'ui' && parts[1] === 'cards' && parts[2] === 'essenceCost') {
    return `../assets/cards/classic/EssenceCost${dprSuffix}.png`;
  }

  if (parts[0] === 'ui' && parts[1] === 'essence' && parts[2] === 'btn') {
    return `../assets/ui/${pack}/essence_btn${dprSuffix}.png`;
  }
  if (parts[0] === 'ui' && parts[1] === 'essence' && parts[2] === 'btn2') {
    return `../assets/ui/${pack}/essence_btn2${dprSuffix}.png`;
  }
  if (parts[0] === 'cards' && parts[1] === 'color') {
    const clr = parts[2];
    const side = parts[3] === 'faceB' ? 'b' : 'a';
    return `../assets/cards/${pack}/color_${clr}_${side}${dprSuffix}.png`;
  }
  if (parts[0] === 'cards' && parts[1] === 'stash' && parts[2] === 'face') {
    const sub = parts[3];
    const side = parts[4] === 'b' ? 'b' : 'a';
    return `../assets/cards/${pack}/stash_${sub}_${side}${dprSuffix}.png`;
  }
  if (parts[0] === 'cards' && parts[1] === 'stash') {
    const name = parts[2] === 'stackTop' ? 'stash_stack_top' : 'stash_back';
    return `../assets/cards/${pack}/${name}${dprSuffix}.png`;
  }
  if (parts[0] === 'cards' && parts[1] === 'back') {
    return `../assets/cards/${pack}/back${dprSuffix}.png`;
  }
  if (parts[0] === 'avatars') {
    const name = parts[1] === 'frame' ? 'frame' : parts[1] === 'player2' ? 'player2' : 'player1';
    return `../assets/avatars/${pack}/${name}${dprSuffix}.png`;
  }
  if (parts[0] === 'ui' && parts[1] === 'nameplate') {
    return `../assets/ui/${pack}/nameplate${dprSuffix}.png`;
  }
  // Tower crystal assets
  if (parts[0] === 'ui' && parts[1] === 'classic') {
    const crystalMap: Record<string, string> = {
      'redCrystal': 'RedCrystal',
      'pinkCrystal': 'PinkCrystal',
      'greenCrystal': 'GreenCrystal',
      'blackCrystal': 'BlackCrystal',
      'purpleCrystal': 'PurpleCrystal',
      'yellowCrystal': 'YellowCrystal',
      'grayCrystal': 'GrayCrystal',
    };
    const name = crystalMap[parts[2]];
    if (name) return `../assets/ui/classic/${name}${dprSuffix}.png`;
  }
  // Effect assets (Broken Secret)
  if (parts[0] === 'effects' && parts[1] === 'brokenSecret' && parts[2] === 'seal') {
    return '../assets/effects/broken_secret/seal.svg';
  }
  return null;
}

function chooseUrl(path2x: string | null, path1x: string | null): string {
  const dpr = typeof window !== 'undefined' && window.devicePixelRatio >= 2;

  const webp2x = path2x ? path2x.replace(/\.png$/, '.webp') : null;
  const webp1x = path1x ? path1x.replace(/\.png$/, '.webp') : null;

  if (dpr) {
    const w2 = webp2x ? getImportedAssetUrl(webp2x) : null;
    if (w2) return w2;
    const p2 = getImportedAssetUrl(path2x);
    if (p2) return p2;
  }
  const w1 = webp1x ? getImportedAssetUrl(webp1x) : null;
  if (w1) return w1;
  const optimized1x = getImportedAssetUrl(path1x);
  if (optimized1x) return optimized1x;
  return PLACEHOLDER;
}

export function getAsset(key: string): string {
  const dpr = typeof window !== 'undefined' && window.devicePixelRatio >= 2;
  const pack = currentPack;
  const p2 = keyToPath(key, pack, '@2x');
  const p1 = keyToPath(key, pack, '');
  return chooseUrl(dpr ? p2 : null, p1);
}

type UpgradeListener = () => void;
const pngUpgradeListeners = new Set<UpgradeListener>();
const loadedPngUpgrades = new Set<string>();

export function subscribeToPngUpgrades(listener: UpgradeListener): () => void {
  pngUpgradeListeners.add(listener);
  return () => { pngUpgradeListeners.delete(listener); };
}

function notifyPngUpgrade(pngUrl: string) {
  loadedPngUpgrades.add(pngUrl);
  pngUpgradeListeners.forEach(l => l());
}

export function getBestAssetUrl(key: string): string {
  const webpUrl = getAsset(key);
  const pngUrl = webpToPngMap.get(webpUrl);
  if (pngUrl && loadedPngUpgrades.has(pngUrl)) {
    return pngUrl;
  }
  return webpUrl;
}

export async function preloadPngUpgrades(concurrency = 2): Promise<void> {
  const upgrades = Array.from(webpToPngMap.entries())
    .filter(([, pngUrl]) => !loadedPngUpgrades.has(pngUrl));

  let i = 0;
  const pump = async () => {
    while (i < upgrades.length) {
      const [, pngUrl] = upgrades[i++];
      try {
        await new Promise<void>((resolve) => {
          const img = new Image();
          img.onload = () => {
            notifyPngUpgrade(pngUrl);
            resolve();
          };
          img.onerror = () => resolve();
          img.src = pngUrl;
        });
      } catch { /* skip */ }
    }
  };

  await Promise.all(Array.from({ length: concurrency }, () => pump()));
}

export function getColorFace(color: CardColor, side: 'A' | 'B' = 'A'): string {
  const slug = colorSlug(color);
  return getAsset(`cards.color.${slug}.${side === 'B' ? 'faceB' : 'faceA'}`);
}

// --- Lazy Preloading ---

const preloaded = new Set<string>();
let inProgress = 0;
let totalToLoad = 0;

function preferDprUrl(pack: PackName, pathNoDpr: string, path2x: string): string | null {
  const use2x = typeof window !== 'undefined' && window.devicePixelRatio >= 2;
  const p2 = path2x.replace('{pack}', pack);
  const p1 = pathNoDpr.replace('{pack}', pack);
  const optimized2x = getImportedAssetUrl(p2);
  const optimized1x = getImportedAssetUrl(p1);
  if (use2x && optimized2x) return optimized2x;
  if (optimized1x) return optimized1x;
  return null;
}

function allPackUrls(pack: PackName): string[] {
  const urls: string[] = [];
  const add = (pathNoDpr: string, path2x: string) => {
    const u = preferDprUrl(pack, pathNoDpr, path2x);
    if (u) urls.push(u);
  };
  add('../assets/ui/{pack}/menu_bg.png', '../assets/ui/{pack}/menu_bg@2x.png');
  add('../assets/ui/{pack}/menu_btn_primary.png', '../assets/ui/{pack}/menu_btn_primary@2x.png');
  add('../assets/ui/{pack}/table_bg_a.png', '../assets/ui/{pack}/table_bg_a@2x.png');
  add('../assets/ui/{pack}/table_bg_b.png', '../assets/ui/{pack}/table_bg_b@2x.png');
  add('../assets/ui/{pack}/nameplate.png', '../assets/ui/{pack}/nameplate@2x.png');
  add('../assets/ui/{pack}/shield.png', '../assets/ui/{pack}/shield@2x.png');
  add('../assets/ui/{pack}/heart.png', '../assets/ui/{pack}/heart@2x.png');

  add('../assets/ui/{pack}/essence_btn.png', '../assets/ui/{pack}/essence_btn@2x.png');
  add('../assets/ui/{pack}/essence_btn2.png', '../assets/ui/{pack}/essence_btn2@2x.png');
  add('../assets/avatars/{pack}/frame.png', '../assets/avatars/{pack}/frame@2x.png');
  add('../assets/avatars/{pack}/player1.png', '../assets/avatars/{pack}/player1@2x.png');
  add('../assets/avatars/{pack}/player2.png', '../assets/avatars/{pack}/player2@2x.png');
  add('../assets/cards/{pack}/back.png', '../assets/cards/{pack}/back@2x.png');
  add('../assets/cards/{pack}/stash_back.png', '../assets/cards/{pack}/stash_back@2x.png');
  add('../assets/cards/{pack}/stash_stack_top.png', '../assets/cards/{pack}/stash_stack_top@2x.png');
  // Color faces A/B
  ['red', 'pink', 'green', 'black', 'purple', 'yellow'].forEach(clr => {
    add(`../assets/cards/{pack}/color_${clr}_a.png`, `../assets/cards/{pack}/color_${clr}_a@2x.png`);
    add(`../assets/cards/{pack}/color_${clr}_b.png`, `../assets/cards/{pack}/color_${clr}_b@2x.png`);
  });
  // Stash faces A/B (subset: we rely on glob to have entries)
  const stashFaces = Object.keys(urlMap).filter(k => k.includes(`/assets/cards/${pack}/stash_`) && k.endsWith('.png'))
    .map(k => urlMap[k]);
  urls.push(...stashFaces);
  // Deduplicate
  return Array.from(new Set(urls));
}

export function preloadAllAssets(options?: { pack?: PackName; concurrency?: number }) {
  const pack = options?.pack ?? currentPack;
  const concurrency = Math.max(4, Math.min(16, options?.concurrency ?? 8));
  const urls = allPackUrls(pack).filter(u => !preloaded.has(u));
  totalToLoad += urls.length;
  let i = 0;
  const pump = () => {
    while (inProgress < concurrency && i < urls.length) {
      const u = urls[i++];
      preloaded.add(u);
      inProgress++;
      const img = new Image();
      img.onload = img.onerror = () => { inProgress--; };
      img.src = u;
    }
    if (i < urls.length) {
      if (typeof (window as any).requestIdleCallback === 'function') {
        (window as any).requestIdleCallback(pump);
      } else {
        setTimeout(pump, 0);
      }
    }
  };
  pump();
}

export function getPreloadProgress(): { loaded: number; total: number; inProgress: number } {
  return { loaded: preloaded.size, total: totalToLoad, inProgress };
}

// --- ENHANCED PRELOADING FOR LAN CLIENTS ---

// Animation spritesheets (from assets/)
const animationAssets = [
  '../assets/optimized_cheers_spritesheet.png',
  '../assets/take_put_mug_spritesheet.png',
  '../assets/frame_00000.png',
];

// Effect sprites (from public/ - use absolute paths)
const effectAssets = [
  '/assets/effects/hammer_slam/hammer_slam_sheet.png',
  '/assets/effects/voodoo_doll/voodoo_doll_anim.png',
];

// Emote sprites (from public/)
const emoteAssets = [
  '/assets/emotes/anger.png',
  '/assets/emotes/piece.png',
  '/assets/emotes/sad.png',
  '/assets/emotes/shocked.png',
  '/assets/emotes/smirk.png',
  '/assets/emotes/thanks.png',
];

function getDeferredProfileAssets(): string[] {
  return DEFERRED_PROFILE_SOURCE_PATHS
    .map(path => getImportedAssetUrl(path))
    .filter((url): url is string => !!url);
}

function getDeferredEmoteAssets(): string[] {
  const urls: string[] = [];

  DEFERRED_EMOTE_IDS.forEach((id) => {
    const importedAsset = getImportedAssetUrl(`../assets/emotes/${id}.png`);
    if (importedAsset) {
      urls.push(importedAsset);
    }
    urls.push(`/assets/emotes/${id}.png`);
    urls.push(`/assets/emotes/animated/${id}.png`);
  });

  return Array.from(new Set(urls));
}

export function getDeferredPreloadUrls(): string[] {
  return Array.from(new Set([
    ...DEFERRED_EFFECT_URLS,
    ...DEFERRED_AUDIO_URLS,
    ...getDeferredProfileAssets(),
    ...getDeferredEmoteAssets(),
  ]));
}

export function isDeferredAssetUrl(url: string): boolean {
  if (DEFERRED_EFFECT_URLS.includes(url)) return true;
  if (DEFERRED_AUDIO_URLS.includes(url)) return true;

  if (/^\/assets\/(?:[1-9]|1[01])lvl(?:-[^/]+)?\.(?:png|webp)$/i.test(url)) {
    return true;
  }

  if (/^\/assets\/(?:anger|piece|sad|shocked|smirk|thanks)(?:-[^/]+)?\.png$/i.test(url)) {
    return true;
  }

  if (/^\/assets\/emotes\/(?:animated\/)?(?:anger|piece|sad|shocked|smirk|thanks)\.png$/i.test(url)) {
    return true;
  }

  return false;
}

// Map layer images (from MapLayers/ - uses import.meta.glob)
let mapLayerFiles: Record<string, any> = {};
try {
  mapLayerFiles = (import.meta as any).glob('../MapLayers/**/*.png', { eager: true }) || {};
} catch {
  mapLayerFiles = {};
}

// Hero portraits (from assets/custom/)
const heroPortraits = [
  '../assets/custom/Verisk.png',
  '../assets/custom/VeriskAvatar.png',
  '../assets/custom/Astrid.png',
  '../assets/custom/AstridAvatar.png',
  '../assets/custom/Erok-Buru.png',
  '../assets/custom/Erok-BuruAvatar.png',
  '../assets/custom/Nyrix.png',
  '../assets/custom/NyrixAvatar.png',
  '../assets/custom/Shanu.png',
  '../assets/custom/ShanuAvatar.png',
  '../assets/custom/Lorian.png',
  '../assets/custom/LorianAvatar.png',
  '../assets/custom/Gemata.png',
  '../assets/custom/GemataAvatar.png',
  '../assets/custom/Ferrim.png',
  '../assets/custom/FerrimAvatar.png',
  '../assets/custom/Norim.png',
  '../assets/custom/NorimAvatar.png',
  '../assets/custom/Evelion.png',
  '../assets/custom/EvelionAvatar.png',
  '../assets/custom/Coril.png',
  '../assets/custom/CorilAvatar.png',
  '../assets/custom/Terrik.png',
  '../assets/custom/TerrikAvatar.png',
  '../assets/custom/Warrior.png',
  '../assets/custom/Mage.png',
  '../assets/custom/Occultist.png',
  '../assets/custom/Harmonized.png',
  '../assets/custom/Chaootic.png',
  '../assets/custom/Untouched.png',
];

/**
 * Get all URLs that need to be preloaded for the game to run smoothly.
 */
function getAllPreloadUrls(pack: PackName): string[] {
  const urls: string[] = [];
  urls.push(...allPackUrls(pack));
  animationAssets.forEach(path => {
    const assetUrl = getImportedAssetUrl(path);
    if (assetUrl) urls.push(assetUrl);
  });
  urls.push(...effectAssets);
  urls.push(...emoteAssets);
  Object.keys(mapLayerFiles).forEach(path => {
    const mod = mapLayerFiles[path] as any;
    if (mod?.default) urls.push(mod.default);
  });
  heroPortraits.forEach(path => {
    const assetUrl = getImportedAssetUrl(path);
    if (assetUrl) urls.push(assetUrl);
  });
  return Array.from(new Set(urls));
}

function getCriticalPreloadUrls(pack: PackName): string[] {
  return getAllPreloadUrls(pack).filter(url => !isDeferredAssetUrl(url));
}

function shouldPreloadWithImage(url: string): boolean {
  return /\.(?:png|jpe?g|webp|gif|svg)(?:\?.*)?$/i.test(url);
}

async function preloadUrlsAsync(
  urls: string[],
  onProgress?: (loaded: number, total: number) => void,
  options?: { concurrency?: number }
): Promise<void> {
  const concurrency = Math.max(2, Math.min(12, options?.concurrency ?? 6));
  const urlsToLoad = urls.filter(u => !preloaded.has(u));

  if (urlsToLoad.length === 0) {
    onProgress?.(1, 1);
    return;
  }

  const total = urlsToLoad.length;
  let loaded = 0;
  let activeLoads = 0;
  let currentIndex = 0;

  return new Promise((resolve) => {
    const loadNext = () => {
      while (activeLoads < concurrency && currentIndex < urlsToLoad.length) {
        const url = urlsToLoad[currentIndex++];
        activeLoads++;

        const onDone = () => {
          preloaded.add(url);
          loaded++;
          activeLoads--;
          onProgress?.(loaded, total);

          if (loaded >= total) {
            resolve();
          } else {
            loadNext();
          }
        };

        if (shouldPreloadWithImage(url)) {
          const img = new Image();
          img.onload = onDone;
          img.onerror = onDone;
          img.src = url;
        } else {
          fetch(url, { credentials: 'same-origin' })
            .then((response) => {
              if (!response.ok) {
                throw new Error(`Failed to preload ${url}: ${response.status}`);
              }
              return response.arrayBuffer();
            })
            .catch(() => undefined)
            .finally(onDone);
        }
      }
    };

    loadNext();
  });
}

/**
 * Preload all game assets with progress callback.
 * Returns a Promise that resolves when all assets are loaded.
 */
export async function preloadAllAssetsAsync(
  onProgress?: (loaded: number, total: number) => void,
  options?: { pack?: PackName; concurrency?: number }
): Promise<void> {
  const pack = options?.pack ?? currentPack;
  const allUrls = getCriticalPreloadUrls(pack);
  return preloadUrlsAsync(allUrls, onProgress, options);
}

export async function preloadDeferredAssetsAsync(options?: { concurrency?: number }): Promise<void> {
  const deferredUrls = getDeferredPreloadUrls();
  await preloadUrlsAsync(deferredUrls, undefined, options);
}
